/**
 * Cumple Sprint 16 — Matriz Annex SL para auditorías IMS.
 *
 * Solo visible cuando assessment.is_integrated=true. Muestra una grilla
 * con las cláusulas 4-10 del Annex SL (estructura compartida por todas
 * las ISO MSS post-2015) en las filas, y las normas seleccionadas en
 * las columnas. Cada celda muestra:
 *   - preguntas respondidas / total para esa cláusula
 *   - findings abiertos
 *   - evidencias adjuntas
 *
 * V1: usa los códigos de los survey_instances + heurística de matching
 * por palabras clave en questionRef contra cláusulas Annex SL.
 *
 * V2 (futuro): cada pregunta del template lleva metadata.iso_clause
 * para mapping preciso. Hoy hacemos best-effort y marcamos celdas no
 * mapeadas con "n/a".
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import { type AssessmentDetail, assessmentsApi } from '../api/assessments.js';
import { findingsApi, type FindingSummary } from '../api/findings.js';
import { Card, LoadingSkeleton, PageHeader } from '../components/ui.js';

interface AnnexSLClause {
  id: string;
  num: string;
  title: string;
  description: string;
}

// Estructura Annex SL (ISO/IEC Directives Part 1, Annex SL Appendix 2)
// Esta es la estructura de alto nivel compartida por TODAS las ISO MSS
// post-2015. Texto público (publicado en directivas ISO).
const ANNEX_SL_CLAUSES: AnnexSLClause[] = [
  {
    id: '4',
    num: '4',
    title: 'Contexto de la organización',
    description: 'Comprensión interna/externa, partes interesadas, alcance del SG.',
  },
  {
    id: '5',
    num: '5',
    title: 'Liderazgo',
    description: 'Compromiso de la dirección, política, roles y responsabilidades.',
  },
  {
    id: '6',
    num: '6',
    title: 'Planificación',
    description: 'Acciones para riesgos y oportunidades, objetivos del SG.',
  },
  {
    id: '7',
    num: '7',
    title: 'Apoyo',
    description: 'Recursos, competencia, conciencia, comunicación, información documentada.',
  },
  { id: '8', num: '8', title: 'Operación', description: 'Planificación y control operacional.' },
  {
    id: '9',
    num: '9',
    title: 'Evaluación del desempeño',
    description: 'Seguimiento, medición, auditoría interna, revisión por la dirección.',
  },
  {
    id: '10',
    num: '10',
    title: 'Mejora',
    description: 'No conformidad, acción correctiva, mejora continua.',
  },
];

export function IsoMatrixPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { setBackTo } = useAppShell();
  const [assessment, setAssessment] = useState<AssessmentDetail | null>(null);
  const [findings, setFindings] = useState<FindingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setBackTo({ href: `/assessments/${id}/capture`, label: 'Levantamiento' });
    return () => setBackTo(null);
  }, [setBackTo, id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([assessmentsApi.get(id), findingsApi.list({ assessment_id: id, limit: 200 })])
      .then(([a, f]) => {
        if (cancelled) return;
        setAssessment(a);
        setFindings(f.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error cargando');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Cada instance representa una norma seleccionada.
  const standards = useMemo(() => assessment?.instances ?? [], [assessment]);

  // Findings abiertos por código de norma (instance template code) por cláusula.
  const findingsMatrix = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const inst of standards) {
      const m = new Map<string, number>();
      for (const c of ANNEX_SL_CLAUSES) m.set(c.id, 0);
      map.set(inst.id, m);
    }
    for (const f of findings) {
      if (f.status === 'closed' || f.status === 'resolved' || f.status === 'dismissed') continue;
      if (f.classification === 'observation' || f.classification === 'ofi') continue;
      // Heurística V1: match por prefijo del question_ref tipo "4.x" o "c4.x".
      const clauseMatch = /(?:^|[^0-9])(\d{1,2})(?:\.[0-9]+)*/.exec(f.question_ref);
      const clauseNum = clauseMatch?.[1];
      if (!clauseNum) continue;
      if (!ANNEX_SL_CLAUSES.some((c) => c.num === clauseNum)) continue;
      const instMap = map.get(f.survey_instance_id);
      if (instMap) instMap.set(clauseNum, (instMap.get(clauseNum) ?? 0) + 1);
    }
    return map;
  }, [findings, standards]);

  if (loading) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <LoadingSkeleton rows={4} />
      </main>
    );
  }

  if (!assessment) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8">
        <p className="text-sm text-rose-600">{error ?? 'No encontrado'}</p>
      </main>
    );
  }

  if (!assessment.is_integrated) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-8 space-y-4">
        <PageHeader
          eyebrow="MATRIZ ANNEX SL"
          title="Esta auditoría no es IMS"
          subtitle="La matriz Annex SL solo aplica a auditorías marcadas como sistemas integrados (IMS) al momento de creación."
        />
        <Link
          to={`/assessments/${assessment.id}/capture`}
          className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          ← Volver al levantamiento
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="MATRIZ ANNEX SL"
        title="Cobertura cruzada de normas integradas"
        subtitle={`${standards.length} norma${standards.length === 1 ? '' : 's'} en esta auditoría IMS. Estructura compartida ISO/IEC Annex SL §4–§10.`}
      />

      {error && (
        <div
          role="alert"
          className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      <Card
        title="Cláusulas Annex SL × normas"
        hint="V1 · Mapping heurístico por número de cláusula en question_ref. Recuento de NC mayor/menor abiertas por celda."
      >
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2 pr-4">
                  Cláusula
                </th>
                {standards.map((s) => (
                  <th
                    key={s.id}
                    className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2 px-3"
                  >
                    {s.template?.code ?? s.template_id.slice(0, 6)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ANNEX_SL_CLAUSES.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 pr-4 align-top">
                    <p className="font-semibold text-brand-900">
                      §{c.num} — {c.title}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">{c.description}</p>
                  </td>
                  {standards.map((s) => {
                    const count = findingsMatrix.get(s.id)?.get(c.num) ?? 0;
                    return (
                      <td key={s.id} className="py-3 px-3 align-top">
                        {count > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700">
                            {count} NC abierta{count === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Normas seleccionadas" hint="Las que conforman este IMS">
        <ul className="space-y-2">
          {standards.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 text-sm">
              <div>
                <p className="font-semibold text-brand-900">
                  {s.template?.name ?? s.template?.code ?? s.template_id}
                </p>
                <p className="text-xs font-mono text-slate-400">
                  {s.template?.code} · v{s.template_version}
                </p>
              </div>
              <span className="text-xs text-slate-500">{s.status}</span>
            </li>
          ))}
        </ul>
      </Card>

      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800">
        <strong>Nota V1:</strong> el mapping entre questionRef y cláusula Annex SL es heurístico
        (busca números 4–10 en el código de la pregunta). Para mapping preciso se requiere metadata{' '}
        <code>iso_clause</code> en cada pregunta del template, planeado en v1.8.
      </div>
    </main>
  );
}
